/**
 * Step 5: Completion/Done view with preview
 */

import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import AndromedaScore from "@/components/AndromedaScore";

export function CompletionStep({ 
  outputType, 
  generatedPosts, 
  generatedVideo,
  onViewScheduled,
  onCreateAnother,
  platform = "instagram",
  hasBranding = false,
  postId = null // Pass the post ID for direct navigation
}) {
  // Get the first post/video for Andromeda scoring
  const firstContent = outputType === "video" ? generatedVideo : generatedPosts[0];
  const caption = firstContent?.caption || firstContent?.content || "";
  const hashtags = firstContent?.hashtags || [];
  const actualPostCount = generatedPosts.length || (firstContent ? 1 : 0);
  
  return (
    <motion.div
      key="step5"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="text-center py-8"
      data-testid="completion-step"
    >
      <div className="w-20 h-20 rounded-full bg-viraly-success/20 flex items-center justify-center mx-auto mb-6">
        <Check className="w-10 h-10 text-viraly-success" />
      </div>
      <h2 className="font-heading text-2xl font-bold mb-2">Content Created!</h2>
      <p className="text-viraly-muted mb-8">
        {outputType === "video" 
          ? "Your video is ready" 
          : actualPostCount > 0 
            ? `${actualPostCount} post${actualPostCount > 1 ? "s" : ""} created`
            : "Your post is ready in the Scheduled tab"
        }
      </p>
      
      {/* Andromeda Score */}
      {caption && (
        <div className="max-w-md mx-auto mb-8">
          <AndromedaScore
            caption={caption}
            hashtags={hashtags}
            hasCta={caption.toLowerCase().includes('link') || caption.toLowerCase().includes('dm') || caption.toLowerCase().includes('click')}
            imageUrl={firstContent?.image_url}
            videoUrl={firstContent?.video_url}
            contentType={outputType}
            platform={platform}
            hasBranding={hasBranding}
            variationCount={generatedPosts?.length || 1}
          />
        </div>
      )}
      
      {/* Video Preview */}
      {outputType === "video" && generatedVideo && (
        <div className="max-w-lg mx-auto mb-8">
          <video 
            src={generatedVideo.video_url} 
            controls 
            className="w-full rounded-xl"
            data-testid="generated-video-preview"
          />
          {generatedVideo.caption && (
            <div className="mt-4 p-4 rounded-xl bg-white/5 text-left">
              <p className="text-xs text-viraly-muted mb-1">Caption:</p>
              <p className="text-sm">{generatedVideo.caption}</p>
            </div>
          )}
        </div>
      )}
      
      {/* Posts Preview */}
      {outputType === "post" && generatedPosts.length > 0 && (
        <div className="max-w-lg mx-auto mb-8 grid gap-4">
          {generatedPosts.slice(0, 2).map((post, i) => (
            <div key={i} className="card-base p-4 text-left" data-testid={`generated-post-${i}`}>
              {post.image_url && (
                <img src={post.image_url} alt="Post" className="w-full h-40 object-cover rounded-lg mb-3" />
              )}
              <p className="text-sm line-clamp-3">{post.caption || post.content}</p>
            </div>
          ))}
          {generatedPosts.length > 2 && (
            <p className="text-sm text-viraly-muted">+{generatedPosts.length - 2} more posts</p>
          )}
        </div>
      )}
      
      {/* Actions */}
      <div className="flex justify-center gap-4">
        <Button 
          onClick={onViewScheduled} 
          className="btn-primary"
          data-testid="view-scheduled-btn"
        >
          See in Scheduled Tab & Approve
        </Button>
        <Button 
          onClick={onCreateAnother} 
          variant="outline" 
          className="border-white/20"
          data-testid="create-another-btn"
        >
          Create Another Post
        </Button>
      </div>
    </motion.div>
  );
}
